import * as React from "react";
import * as moment from "moment";
import { map as _map, isEmpty as _isEmpty, forEach as _forEach } from "lodash";
import { ApiManager } from "../api/ApiManager";
import GlucoseProfiles from "./GlucoseProfiles";
import { GlucoseLevelCode } from "../models/IGlucoseLevels";
import { ICgmReport, IGlucoseBucket } from "../models/ICgmReport";
import BarChartSummary, { ISummaryTopBarData, IBarChartSummary, ISummaryItem } from "../components/common/BarChartSummary";
import { ICgmStats, IDailySugarStatMap, IPatientActivityImpactItem } from "../models/ICgmStats";
import BarChart, { IBarChartData } from "../components/common/Charts/BarChart";
import ActivitiesTable, { IActivitiesTableProps, IActivity } from "../components/AGM/ActivitiesTable";
import AppLoader from "../components/utils/AppLoader";
import { connect } from "react-redux";
import { IAppState } from "../models/IAppState";
import { IPatientDetail } from "@curefit/care-common";
import { AlbusUrlConfigs } from "../api/AlbusUrlConfigs";

interface IAGPReportContainerProps {
    patientProfile: IPatientDetail,
    showHeader: boolean
}

export interface ITopTileData {
    primaryInfo: any;
    unit: any;
    secondaryInfo: string;
}

export enum FoodScoreZone {
    GREEN = "GREEN",
    LIGHT_GREEN = "LIGHT_GREEN",
    AMBER = "AMBER",
    LIGHT_RED = "LIGHT_RED",
    DARK_RED = "DARK_RED",
}

const AGPReportContainer: React.FunctionComponent<IAGPReportContainerProps> = (props) => {
    const [cgmData, setCgmData] = React.useState<ICgmReport>(null);
    const [cgmStats, setCgmStats] = React.useState<ICgmStats>(null);
    const [cgmStatsLoaded, setCgmStatsLoaded] = React.useState<boolean>(false);
    const [cgmDataLoaded, setCgmDataLoaded] = React.useState<boolean>(false);

    React.useEffect(() => {
        fetchCgmReport();
        fetchCgmStats();
    }, [])


    const fetchCgmReport = async () => {
        try {
            const res: ICgmReport = await ApiManager.getAlbusApis().fetchCgmReport(props.patientProfile.curefitUserId);
            sortBucketData(res);
            setCgmDataLoaded(true);

        } catch (e) {
            console.log(`Error in fetching cgm report`, e)
            setCgmDataLoaded(true);
        }
    }

    const fetchCgmStats = async () => {
        try {
            const res: ICgmStats = await ApiManager.getAlbusApis().fetchCgmStats(props.patientProfile.curefitUserId);
            setCgmStats(res);
            setCgmStatsLoaded(true);
        } catch (e) {
            console.log(`Error in fetching cgm stats`, e)
            setCgmStatsLoaded(true);
        }
    }

    const getSummaryTopTileElement = (data: ITopTileData, key: number) => {
        return <div key={key}>
            <div className="primary-info">{data.primaryInfo} <span className="unit">{data.unit}</span> </div>
            <div className="secondary-info">{data.secondaryInfo}</div>
        </div>
    }

    const getTopTileData = (cgmReport: ICgmReport) => {

        return [
            {
                primaryInfo: cgmData.cgmActivePercent.toFixed(0),
                unit: '%',
                secondaryInfo: 'CGM Active'
            },
            {
                primaryInfo: cgmReport.avgGlucose.toFixed(1),
                unit: " mg/dl",
                secondaryInfo: 'Avg glucose'
            },
            {
                primaryInfo: cgmReport.gmiValue.toFixed(0),
                unit: '',
                secondaryInfo: 'Est. HbA1c'
            },

        ]
    }


    const sortBucketData = (report: ICgmReport) => {
        // const data: ITimeRange[] = [];
        const bucketData: IGlucoseBucket[] = [];
        report.glucoseBuckets.forEach((bucket) => {
            bucketData.push({
                ...bucket,
                sortOrder: generateSortOrder(bucket.agmBucketKey),
            })
        })

        // data.sort((a, b) => a.sort_order - b.sort_order);
        bucketData.sort((a, b) => a.sortOrder - b.sortOrder);

        setCgmData({ ...report, glucoseBuckets: bucketData });
    }

    const generateSortOrder = (type: string) => {
        switch (type) {
            case GlucoseLevelCode.VERY_LOW: return 0;
            case GlucoseLevelCode.LOW: return 1;
            case GlucoseLevelCode.HIGH: return 3;
            case GlucoseLevelCode.VERY_HIGH: return 4;
            case GlucoseLevelCode.TARGET_RANGE: return 2;
        }
    }

    const getBucketTypeColor = (type: string): string => {
        switch (type) {
            case GlucoseLevelCode.VERY_LOW: return '#f08840';
            case GlucoseLevelCode.LOW: return '#ffb945';
            case GlucoseLevelCode.HIGH: return '#f47366';
            case GlucoseLevelCode.VERY_HIGH: return '#c15248';
            case GlucoseLevelCode.TARGET_RANGE: return '#7fcb61';
        }
    }

    const getFoodScoreColor = (type: string): string => {
        switch (type) {
            case FoodScoreZone.DARK_RED: return '#cd4342';
            case FoodScoreZone.LIGHT_RED: return '#fea6ac';
            case FoodScoreZone.AMBER: return '#f6daa3';
            case FoodScoreZone.LIGHT_GREEN: return '#99e4e2';
            case FoodScoreZone.GREEN: return '#4fa6a3';
        }
    }

    const getFoodScoreRange = (type: string): string => {
        switch (type) {
            case FoodScoreZone.DARK_RED: return "0 - 2";
            case FoodScoreZone.LIGHT_RED: return "2 - 4";
            case FoodScoreZone.AMBER: return "4 - 6";
            case FoodScoreZone.LIGHT_GREEN: return "6 - 8";
            case FoodScoreZone.GREEN: return "8 - 10";
        }
    }

    const capataliseString = (str: string): string => {
        const strArr = str.toLowerCase().split("_");
        strArr.forEach((s: string, i: number) => strArr[i] = strArr[i][0].toUpperCase() + strArr[i].substring(1));
        return strArr.join(" ");
    }

    const getTimeInHourMin = (mins: number): string => {

        if (mins < 60) return `${mins}min`;

        const hour = Math.floor(mins / 60);
        const res = `${hour}h`;
        const remMins = mins % 60;
        if (!remMins) return res;
        return res + ` ${remMins}min`;
    }

    const formatRange = (low: number, high: number, type: string): string => {
        if (type === GlucoseLevelCode.VERY_LOW) return `<${high}`
        if (type === GlucoseLevelCode.VERY_HIGH) return `>${low}`

        return `${low}-${high}`
    }

    const rearrangeList = (list: IGlucoseBucket[]): IGlucoseBucket[] => {
        const newList: IGlucoseBucket[] = [...list];
        const targetRange: IGlucoseBucket[] = newList.splice(2, 1);
        newList.sort((a, b) => b.sortOrder - a.sortOrder);
        return [...targetRange, ...newList];
    }

    const generateTimeRangesData = (buckets: IGlucoseBucket[]): IBarChartSummary => {

        let barChartSummaryData: IBarChartSummary = {} as IBarChartSummary;

        const heading: string[] = ["Range", "Duration %", "Total Time", "Range Value"];
        barChartSummaryData.headings = heading;
        barChartSummaryData.title = "Time in Ranges";
        barChartSummaryData.barChart = [] as ISummaryTopBarData[];
        barChartSummaryData.rows = [] as ISummaryItem[];

        _forEach(rearrangeList(buckets), (bucket: IGlucoseBucket, index: number) => {

            let chartData: ISummaryTopBarData = {} as ISummaryTopBarData;
            chartData.percent = buckets[index].percentTimeSpent;
            chartData.color = getBucketTypeColor(buckets[index].agmBucketKey);
            barChartSummaryData.barChart.push(chartData);

            let summaryItem: ISummaryItem = {} as ISummaryItem;
            summaryItem.items = [] as string[];
            summaryItem.items.push(capataliseString(bucket.agmBucketKey));
            summaryItem.items.push((bucket.percentTimeSpent && bucket.percentTimeSpent.toFixed(0) + '%') || '-');
            summaryItem.items.push((bucket.timeSpentInMinutes && getTimeInHourMin(bucket.timeSpentInMinutes)) || '-');
            summaryItem.items.push(formatRange(bucket.lowerLimit, bucket.higherLimit, bucket.agmBucketKey));
            summaryItem.color = getBucketTypeColor(bucket.agmBucketKey);

            barChartSummaryData.rows.push(summaryItem);
        });

        return barChartSummaryData;
    }

    const generateFoodDistributionData = (foodScores: any): IBarChartSummary => {

        let barChartSummaryData: IBarChartSummary = {} as IBarChartSummary;

        const heading: string[] = ["Range", "Duration %", "Range Value"];
        barChartSummaryData.headings = heading;
        barChartSummaryData.title = "Zone Scores";
        barChartSummaryData.barChart = [] as ISummaryTopBarData[];
        barChartSummaryData.rows = [] as ISummaryItem[];

        barChartSummaryData.barChart.push({ percent: foodScores[`${FoodScoreZone.GREEN}`] || 0, color: getFoodScoreColor(FoodScoreZone.GREEN) });
        barChartSummaryData.barChart.push({ percent: foodScores[`${FoodScoreZone.LIGHT_GREEN}`] || 0, color: getFoodScoreColor(FoodScoreZone.LIGHT_GREEN) });
        barChartSummaryData.barChart.push({ percent: foodScores[`${FoodScoreZone.AMBER}`] || 0, color: getFoodScoreColor(FoodScoreZone.AMBER) });
        barChartSummaryData.barChart.push({ percent: foodScores[`${FoodScoreZone.LIGHT_RED}`] || 0, color: getFoodScoreColor(FoodScoreZone.LIGHT_RED) });
        barChartSummaryData.barChart.push({ percent: foodScores[`${FoodScoreZone.DARK_RED}`] || 0, color: getFoodScoreColor(FoodScoreZone.DARK_RED) });

        barChartSummaryData.rows.push({
            items: [
                `${capataliseString(FoodScoreZone.GREEN)}`,
                foodScores[`${FoodScoreZone.GREEN}`] && foodScores[`${FoodScoreZone.GREEN}`].toFixed(0) + "%" || "-",
                getFoodScoreRange(FoodScoreZone.GREEN)],
            color: getFoodScoreColor(FoodScoreZone.GREEN)
        });
        barChartSummaryData.rows.push({
            items: [
                `${capataliseString(FoodScoreZone.LIGHT_GREEN)}`,
                foodScores[`${FoodScoreZone.LIGHT_GREEN}`] && foodScores[`${FoodScoreZone.LIGHT_GREEN}`].toFixed(0) + "%" || "-",
                getFoodScoreRange(FoodScoreZone.LIGHT_GREEN)],
            color: getFoodScoreColor(FoodScoreZone.LIGHT_GREEN)
        });
        barChartSummaryData.rows.push({
            items: [
                `${capataliseString(FoodScoreZone.AMBER)}`,
                foodScores[`${FoodScoreZone.AMBER}`] && foodScores[`${FoodScoreZone.AMBER}`].toFixed(0) + "%" || "-",
                getFoodScoreRange(FoodScoreZone.AMBER)],
            color: getFoodScoreColor(FoodScoreZone.AMBER)
        });
        barChartSummaryData.rows.push({
            items: [
                `${capataliseString(FoodScoreZone.LIGHT_RED)}`,
                foodScores[`${FoodScoreZone.LIGHT_RED}`] && foodScores[`${FoodScoreZone.LIGHT_RED}`].toFixed(0) + "%" || "-",
                getFoodScoreRange(FoodScoreZone.LIGHT_RED)],
            color: getFoodScoreColor(FoodScoreZone.LIGHT_RED)
        });
        barChartSummaryData.rows.push({
            items: [
                `${capataliseString(FoodScoreZone.DARK_RED)}`,
                foodScores[`${FoodScoreZone.DARK_RED}`] && foodScores[`${FoodScoreZone.DARK_RED}`].toFixed(0) + "%" || "-",
                getFoodScoreRange(FoodScoreZone.DARK_RED)],
            color: getFoodScoreColor(FoodScoreZone.DARK_RED)
        });

        return barChartSummaryData;
    }

    const getColorFromScore = (score: number): string => {
        if (score >= 100) return "#80c168";
        if (score >= 80) return "#aae692";
        if (score > 40) return "#f6daa3";
        if (score > 20) return "#fea6ac";
        return "#cd4342";
    }

    const generateBarChartData = () => {
        const dailyData: IDailySugarStatMap = cgmStats.dailySugarStatMap;
        const chartData: IBarChartData[] = [] as IBarChartData[];
        for (const date in dailyData) {
            const data: IBarChartData = {} as IBarChartData;
            data.y = dailyData[date].sugarScore;
            data.x = moment(dailyData[date].date).format("DD[']MMM");
            data.barColor = getColorFromScore(dailyData[date].sugarScore);
            chartData.push(data);
        }
        return chartData;
    }


    const generateActivityTableData = (activityList: IPatientActivityImpactItem[], title?: string): IActivitiesTableProps => {

        const mappedActivityList: IActivity[] = [] as IActivity[];
        const activityTypes: string[] = [];

        _forEach(activityList, (activity: IPatientActivityImpactItem) => {
            const currAcitivity: IActivity = {} as IActivity;
            currAcitivity.activityTime = moment(activity.patientActivityLoggingResponse.activityTime).format("DD[']MMM[,] hh:mm A");
            currAcitivity.activityType = activity.patientActivityLoggingResponse.patientActivityType;
            currAcitivity.id = activity.patientActivityLoggingResponse.id;
            currAcitivity.activityDetails = activity.patientActivityLoggingResponse.mealDetails?.notes;
            currAcitivity.score = activity.activityScore;
            currAcitivity.intitalReading = activity.currentGlucoseValue;
            currAcitivity.peakReading = activity.glucosePeak;
            currAcitivity.difference = activity.glucoseChange;
            currAcitivity.timeOverTarget = activity.timeOverTarget;
            currAcitivity.isExpandable = activity.currentGlucoseValue
                || activity.glucosePeak
                || activity.timeOverTarget
                || activity.glucoseChange;

            mappedActivityList.push(currAcitivity);
            if (!activityTypes.includes(activity.patientActivityLoggingResponse.patientActivityType)) {
                activityTypes.push(activity.patientActivityLoggingResponse.patientActivityType);
            }
        });

        return { title, activities: mappedActivityList, isMultiType: false, activityTypes, showPagination: false };
    }

    const agmBodyPadding = props.showHeader === false ? { paddingTop: 0 } : {};
    return (
        <div>
            <div className="patient-agm-container">
                {cgmData?.unSufficientData === false && props.showHeader !== false ? <div className="patient-agm-header">
                    <div className="patient-agm-header__title">AGM Report <span className="patient-agm-header__sub-title">({moment(cgmData.startDate).format("DD MMM YY")} - {moment(cgmData?.endDate).format("DD MMM YY")})</span></div>
                    <div className="patient-agm-header__link">
                        <a href={AlbusUrlConfigs.getCgmReportUrl("" + props.patientProfile.id)} target="_blank"><img src="/images/pdf_icon.svg" alt="pdf" style={{ margin: "0 3px 4px 0" }} /> <span>View Report</span></a>
                    </div>
                </div> : null}
                {cgmData?.unSufficientData === false ? <div className="patient-agm-body" style={agmBodyPadding}>
                    <div className="patient-agm-body__summary">
                        <div className="patient-agm-body__summary--top-tile">{_map(getTopTileData(cgmData), (data: ITopTileData, index: number) => getSummaryTopTileElement(data, index))}</div>
                        <div className="patient-agm-body__summary--time-ranges">
                            {!_isEmpty(cgmData.glucoseBuckets) ? <BarChartSummary  {...generateTimeRangesData(cgmData.glucoseBuckets)} /> : null}
                        </div>
                    </div>
                    <div className="patient-agm-body__chart">
                        <div className="patient-agm-body__chart-header" >Ambulatory Glucose Profile</div>
                        <div className="no-activity-image">
                            <img src="/images/no-activity.svg" alt="no-activity" />
                            <br />
                            <div>No Data available!</div>
                        </div>

                    </div>
                </div> : null}
                {!_isEmpty(cgmStats?.dailySugarStatMap) ? <div className="patient-agm-score-section">
                    <div className="matabolic-score-container"><BarChart header="Daily Metabolic Score" readings={generateBarChartData()} threshold={{ low: 21, high: 90 }} /></div>
                    <div className="zone-score-container">
                        {!_isEmpty(cgmStats.foodScoreDistribution) ? <BarChartSummary  {...generateFoodDistributionData(cgmStats.foodScoreDistribution)} /> : null}
                    </div>
                </div> : null}
                {!_isEmpty(cgmStats?.topScoringFoods) || !_isEmpty(cgmStats?.bottomScoringFoods) ? <div className="patient-food-scoring-section">
                    <div className="top-scoring-meals"><ActivitiesTable {...generateActivityTableData(cgmStats.topScoringFoods, "Top Scoring Food")} /></div>
                    <div className="bottom-scoring-meals"><ActivitiesTable {...generateActivityTableData(cgmStats.bottomScoringFoods, "Bottom Scoring Food")} /></div>

                </div> : null}
            </div>
            {!_isEmpty(cgmStats?.dailySugarStatMap) ? <GlucoseProfiles cgmStats={cgmStats} /> : null}
            {!cgmStatsLoaded && !cgmDataLoaded ? <AppLoader /> :
                cgmData?.unSufficientData === true && _isEmpty(cgmStats?.dailySugarStatMap) ? <div className="no-agm-info">No Data Available!</div> : null}

        </div>
    )
}

const mapStateToProps = (state: IAppState, ownProps: any): IAGPReportContainerProps => {
    return {
        patientProfile: state.patientCompleteInfo.profile,
        showHeader: ownProps.showHeader
    };
};

export default connect(mapStateToProps, null)(AGPReportContainer);

